// src/websocket/adk.ts
import { Auth } from '../auth/auth';
import { Constant } from '../config/constant';
import { CryptoBox } from '../crypto/CryptoBox';
import Socket from './socket'; // Adjust the path as necessary
export default class Adk {
    constructor(config) {
        // public readyPromise: Promise<void>;
        // private readyResolve!: () => void;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectDelay = 3000;
        this.maxDelay = 30000;
        this.isPaused = false;
        this.handleOnConnection = (connection) => {
            this.reconnectAttempts = 0;
            this.reconnectDelay = 3000;
            // Call the hook so child classes can do something here
            this.onConnectedHook(connection);
        };
        this.handleOnClose = () => {
            this.handleReconnection();
        };
        this.handleReconnection = () => {
            if (this.reconnectAttempts < this.maxReconnectAttempts) {
                this.reconnectAttempts++;
                console.log(`Attempting to reconnect in ${this.reconnectDelay / 1000} seconds... (Attempt ${this.reconnectAttempts})`);
                setTimeout(() => {
                    this.connect();
                    // Exponential backoff, capped at 30s
                    this.reconnectDelay = Math.min(this.reconnectDelay * 2, this.maxDelay);
                }, this.reconnectDelay);
            }
            else {
                console.error(`Max reconnection attempts reached. Will retry every ${this.maxDelay / 1000} seconds.`);
                setTimeout(() => {
                    this.connect();
                    // Always use 30s delay after cap
                }, this.maxDelay);
            }
        };
        this.encrypt = this.encrypt.bind(this);
        this.decrypt = this.decrypt.bind(this);
        const rawUrl = (config === null || config === void 0 ? void 0 : config.Uri) || "";
        Constant.BASE_URL = `https://${rawUrl}`;
        Constant.WS_URL = `wss://${rawUrl}/v1/connect`;
        Constant.SSE_URL = `https://${rawUrl}/v1/connect/sse`;
        Constant.LPOLL = `https://${rawUrl}/v1/connect/longpoll`;
        this.socket = Socket.getInstance(this.encrypt, this.decrypt);
        this.adkConfig = config;
        this.on('connection', this.handleOnConnection);
        this.on('close', this.handleOnClose);
    }
    async connect(config) {
        this.initiateSocketConnection();
    }
    pause() {
        if (this.isPaused)
            return;
        this.isPaused = true;
        // stop any reconnection loop
        this.reconnectAttempts = this.maxReconnectAttempts;
        // close the socket
        this.socket.closeWebSocket();
    }
    async resume() {
        if (!this.isPaused)
            return;
        this.isPaused = false;
        // reset backoff/reconnect
        this.reconnectAttempts = 0;
        this.reconnectDelay = 3000;
        // new readyPromise so callers can await
        await this.socket.connectWebSocket();
    }
    async disconnect() {
        // stop any further reconnection attempts
        this.reconnectAttempts = this.maxReconnectAttempts;
        // close the underlying WebSocket
        this.socket.closeWebSocket();
        // remove all event listeners to avoid memory-leaks
        // this.socket.removeAllListeners();
        // reset internal state so you can reconnect later if needed
        this.socket.isConnectionActive = false;
        this.socket.pendingSendMessges = [];
    }
    // public setCredentials(credentials: AuthenticationConfig) {
    //     this.credentials = {
    //         ClientID: credentials.ClientID,
    //         ClientSecret: credentials.ClientSecret,
    //         Environment: credentials.Environment,
    //         OrgTitle: credentials.OrgTitle,
    //         ProjectKey: credentials.ProjectKey,
    //     }
    //     Auth.getInstance().setCredentials(this.credentials)
    // }
    getState() {
        if (this.isPaused) {
            return 'paused';
        }
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            return 'stopped';
        }
        if (this.reconnectAttempts > 0) {
            return 'retrying';
        }
        if (this.socket.isConnectionActive) {
            return 'connected';
        }
        return 'stopped';
    }
    async initiateSocketConnection() {
        var _a, _b;
        let authConfig = {
            ClientID: "",
            ClientSecret: "",
            Environment: "",
            OrgTitle: "",
            ProjectKey: "",
            AccessToken: ""
        };
        if (!((_a = this.adkConfig) === null || _a === void 0 ? void 0 : _a.getCredentials)) {
            authConfig = await this.loadConfig();
        }
        authConfig.config = this.adkConfig,
            authConfig.getCredentials = (_b = this.adkConfig) === null || _b === void 0 ? void 0 : _b.getCredentials;
        this.socket.initiateSocket(authConfig);
    }
    // Generic method implementation
    on(event, callback) {
        var _a;
        (_a = this.socket) === null || _a === void 0 ? void 0 : _a.on(event, callback);
        return this;
    }
    // Generic method implementation
    off(event, callback) {
        var _a;
        (_a = this.socket) === null || _a === void 0 ? void 0 : _a.off(event, callback);
        return this;
    }
    /**
     * Subscribe to a specific channel via Socket.
     * @param channel The channel name to subscribe to.
     * @returns A Subscription instance for the specified channel.
     */
    subscribe(channel) {
        return this.socket.subscribe(channel);
    }
    presence(channel) {
        return this.socket.presence(channel);
    }
    /**
     * Register an interceptor via Socket.
     * @param interceptor The interceptor name to subscribe to.
     * @returns A interception instance for the specified interceptor.
     */
    async intercept(interceptor, fn) {
        return await this.socket.intercept(interceptor, fn);
    }
    /**
     * Close the WebSocket connection.
     */
    closeWebSocket() {
        this.socket.closeWebSocket();
    }
    async listenForSecureLine(event) {
        return this.socket.listenForSecureLine(event);
    }
    async pushForSecureLine(event, data) {
        return this.socket.pushForSecureLine(event, data);
    }
    /**
     * Hook that the child class can override if needed.
     * This is called whenever a 'connection' event is received
     */
    onConnectedHook(connection) {
        // Default no-op. Child can override.
    }
    /**
     * Method used to encrypt the message.
     * This is called whenever a message is sent through secure channel subsciption
     */
    async encrypt(data, receipientPublicKey) {
        if (!this.myKeyPair) {
            throw Error("Please generate a new key pair or set an existing key pair");
        }
        const encrypted = CryptoBox.encrypt(data, receipientPublicKey, this.myKeyPair.privateKey);
        return encrypted;
    }
    /**
     * Method used to decrypt the message.
     * This is called whenever a message is recieved through secure channel
     */
    async decrypt(data, senderPublicKey) {
        if (!this.myKeyPair) {
            throw Error("Please generate a new key pair or set an existing key pair");
        }
        const encrypted = CryptoBox.decrypt(data, senderPublicKey, this.myKeyPair.privateKey);
        return encrypted;
    }
    async loadConfig() {
        const response = await fetch(Constant.CONFIG_JSON_PATH);
        if (!response.ok) {
            throw new Error('Failed to load configuration');
        }
        const credentials = await response.json();
        return {
            ClientID: credentials['Client-ID'],
            ClientSecret: credentials['Client-Secret'],
            Environment: credentials['Environment'],
            OrgTitle: credentials['Org-Title'],
            ProjectKey: credentials['ProjectKey'],
        };
    }
    async savePublicKey(keyPair) {
        const auth = Auth.getInstance();
        const authData = await auth.getAuthData();
        const credentials = auth.getCredentials();
        const response = await fetch(Constant.BASE_URL + '/v1/update-publickey', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Org': credentials.OrgTitle,
                "Authorization": `Bearer ${authData.AccessToken}`,
                "Environment": credentials.Environment,
                "ProjectKey": credentials.ProjectKey,
            },
            body: JSON.stringify({
                public_key: keyPair.publicKey
            })
        });
        if (!response.ok) {
            throw Error("Error updating keypair");
        }
        this.myKeyPair = keyPair;
        const data = await response.json();
        return data;
    }
    async generateKeyPair() {
        const myKeyPair = await CryptoBox.generateKeyPair();
        return myKeyPair;
    }
    async setKeyPair(keyPair) {
        if (!keyPair ||
            typeof keyPair.publicKey !== "string" ||
            typeof keyPair.privateKey !== "string" ||
            !keyPair.publicKey ||
            !keyPair.privateKey) {
            throw new Error("Invalid KeyPair: PublicKey and PrivateKey must be non-empty strings");
        }
        this.savePublicKey(keyPair);
    }
    call(endpoint, options = {}) {
        return new Promise(async (resolve, reject) => {
            var _a;
            try {
                // 1) grab fresh token
                const auth = Auth.getInstance();
                await auth.authenticate();
                const { AccessToken } = await auth.getAuthData();
                const credentials = auth.getCredentials();
                // 2) build URL + optional ?foo=bar
                let url = `${Constant.BASE_URL}${endpoint}`;
                if (options.queryParams) {
                    url += `?${new URLSearchParams(options.queryParams).toString()}`;
                }
                // 3) assemble headers
                const headers = {
                    'Authorization': `Bearer ${AccessToken}`,
                    'Accept': 'application/json',
                    "X-Org": credentials.OrgTitle,
                    "Environment": credentials.Environment,
                    "ProjectKey": credentials.ProjectKey,
                    ...options.headers,
                };
                // 4) stringify payload if present
                let body;
                if (options.payload != null) {
                    headers['Content-Type'] = 'application/json';
                    body = JSON.stringify(options.payload);
                }
                // 5) fire off fetch
                const res = await fetch(url, {
                    method: ((_a = options.method) !== null && _a !== void 0 ? _a : 'GET').toUpperCase(),
                    headers,
                    body,
                });
                // 6) error handling
                if (!res.ok) {
                    let errMsg;
                    try {
                        const errBody = await res.json();
                        errMsg = errBody.message || JSON.stringify(errBody);
                    }
                    catch (_b) {
                        errMsg = res.statusText;
                    }
                    return reject(new Error(`API ${endpoint} failed: ${errMsg}`));
                }
                // 7) handle 204 No Content
                if (res.status === 204) {
                    return resolve(undefined);
                }
                // 8) parse and resolve
                const data = (await res.json());
                resolve(data);
            }
            catch (err) {
                reject(err);
            }
        });
    }
}
